import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Link2 } from "lucide-react";
import { FUNNELS } from "@/lib/funnels";

const RANGES = [7, 30, 90];
const pct = (a, b) => (b ? `${Math.min(100, Math.round((a / b) * 100))}%` : "–");
const LEGEND_ORDER = ["visits", "quizStarts", "leads"];

const LinkBuilder = ({ t }) => {
  const [seg, setSeg] = useState("student");
  const [src, setSrc] = useState("");
  const slug = src.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const link = `${window.location.origin}${FUNNELS[seg].path}${slug ? `?src=${slug}` : ""}`;
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success(t("admin.linkCopied")));
  return (
    <Card className="border-slate-200 p-5" data-testid="link-builder">
      <p className="flex items-center gap-2 font-heading font-bold text-slate-900"><Link2 className="h-4 w-4 text-amber-600" /> {t("admin.linkTitle")}</p>
      <p className="mt-1 text-sm text-slate-500">{t("admin.linkHelp")}</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-[180px_1fr_auto]">
        <Select value={seg} onValueChange={setSeg}>
          <SelectTrigger data-testid="link-builder-segment"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.keys(FUNNELS).map((s) => <SelectItem key={s} value={s}>{t(`admin.funnelSeg_${s}`)}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={src} onChange={(e) => setSrc(e.target.value)} placeholder={t("admin.linkPlaceholder")} data-testid="link-builder-source" />
        <Button onClick={copy} className="bg-amber-600 hover:bg-amber-700" data-testid="link-builder-copy"><Copy className="mr-1.5 h-4 w-4" /> {t("admin.linkCopy")}</Button>
      </div>
      <p className="mt-2 break-all rounded-md bg-slate-50 px-3 py-2 font-mono text-xs text-slate-700" data-testid="link-builder-output">{link}</p>
    </Card>
  );
};

const Channels = ({ rows, t }) => (
  <Card className="border-slate-200 p-5" data-testid="funnel-channels">
    <p className="font-heading font-bold text-slate-900">{t("admin.topSources")}</p>
    {rows.length === 0 ? <p className="mt-3 text-sm text-slate-500">{t("admin.noSources")}</p> : (
      <table className="mt-3 w-full text-sm">
        <thead><tr className="text-left text-slate-500"><th className="py-1">{t("admin.source")}</th><th>{t("admin.visits")}</th><th>{t("admin.leads")}</th><th>{t("admin.hotLeads")}</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.channel} className="border-t border-slate-100" data-testid="funnel-channel-row"><td className="max-w-[180px] truncate py-1.5 font-semibold text-slate-800" title={r.channel}>{r.channel}</td><td>{r.visits}</td><td>{r.leads}</td><td className="text-rose-600 font-semibold">{r.hot}</td></tr>
        ))}</tbody>
      </table>
    )}
  </Card>
);

export const FunnelStats = () => {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/funnel-stats", { params: { days } }).then(({ data: d }) => setData(d)).catch(() => setData({ segments: [], channels: [] }));
  }, [days]);
  if (!data) return null;
  const chart = data.segments.map((s) => ({ name: t(`admin.funnelSeg_${s.segment}`), [t("admin.visits")]: s.visits, [t("admin.quizStarts")]: s.quiz_starts, [t("admin.leads")]: s.leads }));
  return (
    <div className="space-y-4" data-testid="funnel-stats">
      <Card className="border-slate-200 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-heading font-bold text-slate-900">{t("admin.funnelStatsTitle")}</p>
          <div className="flex gap-1">{RANGES.map((d) => <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => setDays(d)} data-testid={`funnel-stats-range-${d}`}>{d}d</Button>)}</div>
        </div>
        <div className="mt-4 h-64" data-testid="funnel-stats-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend itemSorter={(item) => LEGEND_ORDER.findIndex((k) => t(`admin.${k}`) === item.value)} />
              <Bar dataKey={t("admin.visits")} fill="#38bdf8" radius={[6, 6, 0, 0]} />
              <Bar dataKey={t("admin.quizStarts")} fill="#f59e0b" radius={[6, 6, 0, 0]} />
              <Bar dataKey={t("admin.leads")} fill="#10b981" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {data.segments.map((s) => (
            <div key={s.segment} className="rounded-xl bg-slate-50 p-3 text-sm" data-testid={`funnel-stats-${s.segment}`}>
              <p className="font-bold text-slate-900">{t(`admin.funnelSeg_${s.segment}`)}</p>
              <p className="text-slate-600">{t("admin.startRate")}: <b>{pct(s.quiz_starts, s.visits)}</b> · {t("admin.leadRate")}: <b>{pct(s.leads, s.visits)}</b></p>
              <p className="text-rose-600 font-semibold">{s.hot} {t("admin.hotLeads")}</p>
            </div>
          ))}
        </div>
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        <Channels rows={data.channels} t={t} />
        <LinkBuilder t={t} />
      </div>
    </div>
  );
};
